/**
 * Pure date utility functions for Gantt chart
 */
import { addMinutes } from 'date-fns';
import { isoDateFormat } from 'helpers/utils';

export const parseDate = (dateValue) => {
	if (!dateValue) return null;

	// Handle UTC dates from backend - add 'Z' if not present to ensure UTC interpretation
	// The browser will automatically convert to local time
	if (typeof dateValue === 'string') {
		const dateWithZ = dateValue[dateValue.length - 1] === 'Z'
			? dateValue
			: dateValue + 'Z';
		const date = new Date(dateWithZ);
		return isNaN(date.getTime()) ? null : date;
	}

	const date = new Date(dateValue);
	return isNaN(date.getTime()) ? null : date;
};

export const calculateEndDate = (startDate, estimatedMinutes) => {
	const start = parseDate(startDate);
	if (!start) return null;
	if (!estimatedMinutes) return start;
	return addMinutes(start, estimatedMinutes);
};

export const calculateDateRange = (tasks) => {
	if (!tasks || tasks.length === 0) {
		return { start: null, end: null };
	}

	let earliestStart = null;
	let latestEnd = null;

	tasks.forEach((task) => {
		if (task.start) {
			const taskStart = new Date(task.start);
			if (!earliestStart || taskStart < earliestStart) {
				earliestStart = taskStart;
			}
		}
		if (task.end) {
			const taskEnd = new Date(task.end);
			if (!latestEnd || taskEnd > latestEnd) {
				latestEnd = taskEnd;
			}
		}
	});

	return { start: earliestStart, end: latestEnd };
};

const HOUR_MS = 1000 * 60 * 60;
const MIN_RANGE_PADDING_MS = 12 * HOUR_MS;
const MAX_RANGE_PADDING_MS = 30 * 24 * HOUR_MS;

/**
 * Widens a range so the chart has time either side of the data.
 *
 * The Gantt jumps to a coarser scale as soon as the range stops filling the
 * viewport, so a scale pinned exactly to the data leaves no room to zoom out
 * smoothly.
 */
export const padDateRange = ({ start, end } = {}, ratio = 0.5) => {
	if (!start || !end) return { start: start ?? null, end: end ?? null };

	const padding = Math.min(
		Math.max((end.getTime() - start.getTime()) * ratio, MIN_RANGE_PADDING_MS),
		MAX_RANGE_PADDING_MS
	);

	return {
		start: new Date(start.getTime() - padding),
		end: new Date(end.getTime() + padding),
	};
};

export const formatDateForInput = (dateString) => {
	if (!dateString) return '';
	try {
		let date;
		if (dateString.includes('T')) {
			date = new Date(dateString);
		} else {
			const dateWithZ = dateString[dateString.length - 1] === "Z"
				? dateString
				: dateString + "Z";
			const isoDate = isoDateFormat(dateWithZ);
			date = new Date(isoDate);
		}
		
		if (isNaN(date.getTime())) {
			console.warn('Invalid date:', dateString);
			return '';
		}
		
		return date.toISOString().slice(0, 16);
	} catch (error) {
		console.warn('Error formatting date:', dateString, error);
		return '';
	}
};
const pad = (value) => String(value).padStart(2, "0");

export const toTimeInputValue = (date) =>
	date instanceof Date && !isNaN(date.getTime())
		? `${pad(date.getHours())}:${pad(date.getMinutes())}`
		: "";

export const withTimeOfDay = (date, time) => {
	const match = /^(\d{2}):(\d{2})/.exec(time || "");
	if (!date || !match) return date;

	const result = new Date(date);
	result.setHours(Number(match[1]), Number(match[2]), 0, 0);
	return result;
};
